/**
 * Draft Tasks API service - handles AI inbox and draft task operations
 */

import { apiClient } from './client';

// Enums
export type DraftTaskStatus = 'pending' | 'approved' | 'rejected' | 'modified';
export type ExtractionSource = 'meeting_transcript' | 'email' | 'chat' | 'document' | 'manual';

// Types
export interface DraftTask {
  id: string;
  organizationId: string;
  projectId?: string | undefined;
  sourceType: ExtractionSource;
  sourceMeetingId?: string | undefined;
  sourceEmailId?: string | undefined;
  sourceLanguage?: string | undefined;
  title: string;
  description?: string | undefined;
  suggestedPriority?: string | undefined;
  suggestedDueDate?: string | undefined;
  suggestedAssigneeName?: string | undefined;
  suggestedAssigneeId?: string | undefined;
  confidenceScore?: number | undefined;
  extractionReasoning?: string | undefined;
  sourceText?: string | undefined;
  sourceTimestampSeconds?: number | undefined;
  status: DraftTaskStatus;
  reviewedById?: string | undefined;
  reviewNotes?: string | undefined;
  createdTaskId?: string | undefined;
  aiModel?: string | undefined;
  aiProvider?: string | undefined;
  createdById?: string | undefined;
  createdAt: string;
  updatedAt: string;
}

export interface DraftTaskDetail extends DraftTask {
  sourceMeetingTitle?: string | undefined;
  sourceEmailSubject?: string | undefined;
  suggestedAssigneeNameResolved?: string | undefined;
  createdTaskKey?: string | undefined;
}

export interface AIInboxStats {
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  totalCount: number;
}

// Request/Response types
export interface DraftTaskListResponse {
  items: DraftTask[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface CreateDraftTaskRequest {
  organizationId: string;
  title: string;
  description?: string | undefined;
  projectId?: string | undefined;
  sourceType?: ExtractionSource | undefined;
  sourceMeetingId?: string | undefined;
  suggestedPriority?: string | undefined;
  suggestedDueDate?: string | undefined;
  suggestedAssigneeName?: string | undefined;
}

export interface ApproveDraftTaskRequest {
  boardId: string;
  columnId: string;
  title?: string | undefined;
  description?: string | undefined;
  priority?: string | undefined;
  assigneeId?: string | undefined;
  dueDate?: string | undefined;
  reviewNotes?: string | undefined;
}

export interface RejectDraftTaskRequest {
  reviewNotes?: string | undefined;
}

export interface BulkApproveRequest {
  draftTaskIds: string[];
  boardId: string;
  columnId: string;
  assigneeId?: string | undefined;
}

export interface BulkRejectRequest {
  draftTaskIds: string[];
  reviewNotes?: string | undefined;
}

export interface BulkDeleteRequest {
  draftTaskIds: string[];
}

export interface BulkDeleteResponse {
  deletedCount: number;
}

export interface ApprovalResponse {
  draftTask: DraftTask;
  createdTaskId: string;
  createdTaskKey: string;
}

export interface BulkApprovalResponse {
  approvedCount: number;
  createdTaskIds: string[];
}

export interface BulkRejectionResponse {
  rejectedCount: number;
}

export interface DraftTaskFilterParams {
  organizationId: string;
  status?: DraftTaskStatus | undefined;
  projectId?: string | undefined;
  meetingId?: string | undefined;
  emailId?: string | undefined;
}

/**
 * Draft Tasks API methods
 */
export const draftTasksApi = {
  // =====================
  // Stats
  // =====================

  /**
   * Get AI inbox statistics
   */
  async getStats(organizationId: string): Promise<AIInboxStats> {
    const response = await apiClient.get<any>('/draft-tasks/stats', {
      params: { organization_id: organizationId },
    });
    const data = response.data;

    return {
      pendingCount: data.pendingCount ?? data.pending_count,
      approvedCount: data.approvedCount ?? data.approved_count,
      rejectedCount: data.rejectedCount ?? data.rejected_count,
      totalCount: data.totalCount ?? data.total_count,
    };
  },

  // =====================
  // CRUD
  // =====================

  /**
   * List draft tasks (AI inbox)
   */
  async list(
    filters: DraftTaskFilterParams,
    page = 1,
    pageSize = 50
  ): Promise<DraftTaskListResponse> {
    const params: Record<string, string | number> = {
      organization_id: filters.organizationId,
      page,
      page_size: pageSize,
    };
    if (filters.status) params.status = filters.status;
    if (filters.projectId) params.project_id = filters.projectId;
    if (filters.meetingId) params.meeting_id = filters.meetingId;
    if (filters.emailId) params.email_id = filters.emailId;

    const response = await apiClient.get<any>('/draft-tasks', { params });
    const data = response.data;

    return {
      items: (data.items ?? []).map(transformDraftTask),
      total: data.total,
      page: data.page,
      pageSize: data.pageSize ?? data.page_size,
      hasMore: data.hasMore ?? data.has_more,
    };
  },

  /**
   * Get draft task by ID
   */
  async get(draftTaskId: string): Promise<DraftTaskDetail> {
    const response = await apiClient.get<unknown>(`/draft-tasks/${draftTaskId}`);
    return transformDraftTaskDetail(response.data);
  },

  /**
   * Create a draft task manually
   */
  async create(data: CreateDraftTaskRequest): Promise<DraftTask> {
    const response = await apiClient.post<unknown>('/draft-tasks', {
      organization_id: data.organizationId,
      title: data.title,
      description: data.description,
      project_id: data.projectId,
      source_type: data.sourceType || 'manual',
      source_meeting_id: data.sourceMeetingId,
      suggested_priority: data.suggestedPriority,
      suggested_due_date: data.suggestedDueDate,
      suggested_assignee_name: data.suggestedAssigneeName,
    });
    return transformDraftTask(response.data);
  },

  /**
   * Delete a draft task
   */
  async delete(draftTaskId: string): Promise<void> {
    await apiClient.delete(`/draft-tasks/${draftTaskId}`);
  },

  // =====================
  // Approve/Reject
  // =====================

  /**
   * Approve a draft task and create a real task
   */
  async approve(
    draftTaskId: string,
    data: ApproveDraftTaskRequest
  ): Promise<ApprovalResponse> {
    const response = await apiClient.post<any>(`/draft-tasks/${draftTaskId}/approve`, {
      board_id: data.boardId,
      column_id: data.columnId,
      title: data.title,
      description: data.description,
      priority: data.priority,
      assignee_id: data.assigneeId,
      due_date: data.dueDate,
      review_notes: data.reviewNotes,
    });
    const respData = response.data;

    return {
      draftTask: transformDraftTask(respData.draftTask ?? respData.draft_task),
      createdTaskId: respData.createdTaskId ?? respData.created_task_id,
      createdTaskKey: respData.createdTaskKey ?? respData.created_task_key,
    };
  },

  /**
   * Reject a draft task
   */
  async reject(draftTaskId: string, data: RejectDraftTaskRequest): Promise<DraftTask> {
    const response = await apiClient.post<unknown>(`/draft-tasks/${draftTaskId}/reject`, {
      review_notes: data.reviewNotes,
    });
    return transformDraftTask(response.data);
  },

  // =====================
  // Bulk Actions
  // =====================

  /**
   * Approve multiple draft tasks
   */
  async bulkApprove(data: BulkApproveRequest): Promise<BulkApprovalResponse> {
    const response = await apiClient.post<any>('/draft-tasks/bulk/approve', {
      draft_task_ids: data.draftTaskIds,
      board_id: data.boardId,
      column_id: data.columnId,
      assignee_id: data.assigneeId,
    });
    const respData = response.data;

    return {
      approvedCount: respData.approvedCount ?? respData.approved_count,
      createdTaskIds: respData.createdTaskIds ?? respData.created_task_ids,
    };
  },

  /**
   * Reject multiple draft tasks
   */
  async bulkReject(data: BulkRejectRequest): Promise<BulkRejectionResponse> {
    const response = await apiClient.post<any>('/draft-tasks/bulk/reject', {
      draft_task_ids: data.draftTaskIds,
      review_notes: data.reviewNotes,
    });
    const respData = response.data;

    return {
      rejectedCount: respData.rejectedCount ?? respData.rejected_count,
    };
  },

  /**
   * Delete multiple draft tasks
   */
  async bulkDelete(data: BulkDeleteRequest): Promise<BulkDeleteResponse> {
    const response = await apiClient.post<any>('/draft-tasks/bulk/delete', {
      draft_task_ids: data.draftTaskIds,
    });
    const respData = response.data;

    return {
      deletedCount: respData.deletedCount ?? respData.deleted_count,
    };
  },
};

// Transform functions
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformDraftTask(data: unknown): DraftTask {
  const d = data as Record<string, unknown>;
  return {
    id: d.id as string,
    organizationId: (d.organizationId ?? d.organization_id) as string,
    projectId: (d.projectId ?? d.project_id) as string | undefined,
    sourceType: (d.sourceType ?? d.source_type) as ExtractionSource,
    sourceMeetingId: (d.sourceMeetingId ?? d.source_meeting_id) as string | undefined,
    sourceEmailId: (d.sourceEmailId ?? d.source_email_id) as string | undefined,
    sourceLanguage: (d.sourceLanguage ?? d.source_language) as string | undefined,
    title: d.title as string,
    description: d.description as string | undefined,
    suggestedPriority: (d.suggestedPriority ?? d.suggested_priority) as string | undefined,
    suggestedDueDate: (d.suggestedDueDate ?? d.suggested_due_date) as string | undefined,
    suggestedAssigneeName: (d.suggestedAssigneeName ?? d.suggested_assignee_name) as string | undefined,
    suggestedAssigneeId: (d.suggestedAssigneeId ?? d.suggested_assignee_id) as string | undefined,
    confidenceScore: (d.confidenceScore ?? d.confidence_score) as number | undefined,
    extractionReasoning: (d.extractionReasoning ?? d.extraction_reasoning) as string | undefined,
    sourceText: (d.sourceText ?? d.source_text) as string | undefined,
    sourceTimestampSeconds: (d.sourceTimestampSeconds ?? d.source_timestamp_seconds) as number | undefined,
    status: d.status as DraftTaskStatus,
    reviewedById: (d.reviewedById ?? d.reviewed_by_id) as string | undefined,
    reviewNotes: (d.reviewNotes ?? d.review_notes) as string | undefined,
    createdTaskId: (d.createdTaskId ?? d.created_task_id) as string | undefined,
    aiModel: (d.aiModel ?? d.ai_model) as string | undefined,
    aiProvider: (d.aiProvider ?? d.ai_provider) as string | undefined,
    createdById: (d.createdById ?? d.created_by_id) as string | undefined,
    createdAt: (d.createdAt ?? d.created_at) as string,
    updatedAt: (d.updatedAt ?? d.updated_at) as string,
  };
}

function transformDraftTaskDetail(data: unknown): DraftTaskDetail {
  const d = data as Record<string, unknown>;
  const base = transformDraftTask(data);
  return {
    ...base,
    sourceMeetingTitle: (d.sourceMeetingTitle ?? d.source_meeting_title) as string | undefined,
    sourceEmailSubject: (d.sourceEmailSubject ?? d.source_email_subject) as string | undefined,
    suggestedAssigneeNameResolved: (d.suggestedAssigneeNameResolved ?? d.suggested_assignee_name_resolved) as string | undefined,
    createdTaskKey: (d.createdTaskKey ?? d.created_task_key) as string | undefined,
  };
}

export default draftTasksApi;
